import { CheckCircleIcon, FlagIcon, MagnifyingGlassIcon, RectangleStackIcon } from "@heroicons/react/20/solid";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import type { SearchResult } from "@/lib/types";
import { cn, taskKey } from "@/lib/utils";
import { entityPath } from "./NotificationsButton";
import { Modal } from "./ui/Modal";

export const entityIcons = { epic: FlagIcon, sprint: RectangleStackIcon, task: CheckCircleIcon };

export function useDebounced<T>(value: T, ms = 200) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function useSearch(q: string, enabled = true) {
  const { pid, bundle } = useProject();
  const term = useDebounced(q.trim());
  // allow searching by task key, e.g. "UNI-12"
  const prefix = `${bundle.project.key.toLowerCase()}-`;
  const normalized = term.toLowerCase().startsWith(prefix) ? term.slice(prefix.length) : term;
  return useQuery({
    queryKey: ["p", pid, "search", normalized],
    queryFn: () => api.search(pid, normalized),
    enabled: enabled && normalized.length > 0,
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });
}

export function SearchResultRow({ r, active, onClick }: { r: SearchResult; active?: boolean; onClick: () => void }) {
  const { bundle } = useProject();
  const Icon = entityIcons[r.entityType];
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-start text-sm hover:bg-hover", active && "bg-hover")}
    >
      <Icon className="size-4 shrink-0 text-muted" />
      {r.number != null && <span className="shrink-0 text-xs text-faint tabular-nums">{taskKey(bundle.project.key, r.number)}</span>}
      <span className="min-w-0 flex-1 truncate" dir="auto">
        {r.title}
      </span>
      <span className="text-xs text-faint capitalize">{r.entityType}</span>
    </button>
  );
}

export function SearchDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { pid } = useProject();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const results = useSearch(q, open);
  const items = q.trim() ? (results.data ?? []) : [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);
  useEffect(() => setActive(0), [results.data]);

  const go = (r: SearchResult) => {
    onOpenChange(false);
    navigate(entityPath(pid, r.entityType, r.id));
  };

  return (
    <Modal open={open} onClose={() => onOpenChange(false)} size="lg" className="overflow-hidden">
      <div className="-mx-5 -my-4">
        <div className="flex items-center gap-2 border-b border-line px-4">
          <MagnifyingGlassIcon className="size-5 text-muted" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, items.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter" && items[active]) {
                go(items[active]);
              }
            }}
            placeholder="Search epics, sprints and tasks…"
            className="h-12 flex-1 bg-transparent text-base outline-none placeholder:text-faint"
            dir="auto"
          />
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2 scroll-thin">
          {!q.trim() && <div className="px-2 py-6 text-center text-sm text-muted">Type a title or a task key like {"“"}12{"”"}</div>}
          {q.trim() && results.data && items.length === 0 && <div className="px-2 py-6 text-center text-sm text-muted">No results</div>}
          {items.map((r, i) => (
            <SearchResultRow key={r.id} r={r} active={i === active} onClick={() => go(r)} />
          ))}
        </div>
      </div>
    </Modal>
  );
}
